import React from 'react';
import { CardMedia, Typography, Box, IconButton } from '@mui/material';
import { IProject } from '../../../types/global/helper';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import { useThemeContext } from '../../../hook/useThemeManager';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { theme } from '../../../constants/theme';

export interface DemoReelCardProps {
    project: IProject;
    isEditMode?: boolean;
    onDelete?: (project: IProject) => void;
    onClickPlay?: (project: IProject) => void;
}

const DemoReelCard: React.FC<DemoReelCardProps> = ({ project, isEditMode = false, onDelete, onClickPlay }) => {
    const { themeStyle } = useThemeContext();

    return (
        <Box
            sx={{
                position: 'relative',
                width: 320,
                height: 180,
                borderRadius: 3,
                overflow: 'hidden',
                boxShadow: 3,
                bgcolor: theme[themeStyle].pure_black,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                mb: 2,
            }}>
            {/* Delete Button (Only in Edit Mode) */}
            {isEditMode && (
                <CloseIcon
                    fontSize="medium"
                    onClick={() => onDelete && onDelete(project)}
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        backgroundColor: 'rgba(0,0,0,0.7)',
                        color: theme[themeStyle].true_white,
                        width: 32,
                        height: 32,
                        zIndex: 9999,
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.9)' },
                    }}
                />
            )}
            {/* Thumbnail */}
            <CardMedia
                component="img"
                sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    zIndex: 1,
                }}
                image={project.feature_image}
                alt={project.title}
            />

            {/* Gradient Overlay */}
            <Box
                sx={{
                    position: 'absolute',
                    left: 0,
                    bottom: 0,
                    width: '100%',
                    height: '45%',
                    zIndex: 2,
                    background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.7) 100%)',
                }}
            />
            {/* Play Button Overlay (Visible only if media_type is 'video') */}
            {project.media_type === 'video' && (
                <IconButton
                    onClick={() => onClickPlay && onClickPlay(project)}
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        color: theme[themeStyle].true_white,
                        zIndex: 3,
                        p: 0,
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
                    }}>
                    <PlayCircleOutlineIcon sx={{ fontSize: 64 }} />
                </IconButton>
            )}

            {/* Title & Description */}
            <Box
                sx={{
                    position: 'absolute',
                    left: 0,
                    bottom: 0,
                    width: '100%',
                    zIndex: 4,
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                }}>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 16,
                        color: theme[themeStyle].true_white,
                        textShadow: '0 2px 8px rgba(0,0,0,0.7)',
                        mb: 0.5,
                    }}>
                    {project.title}
                </Typography>
            </Box>
        </Box>
    );
};

export { DemoReelCard };
